<template>
  <div class="app-container">
    <div class="module-layout">
      <!-- Parameter Panel -->
      <div class="parameter-panel">
        <h4><i class="el-icon-setting"></i> Parameters</h4>
        <div class="parameter-container">
          <!-- Sample Type Selection -->
          <div class="parameter-group">
            <label>Sample Site</label>
            <div class="sample-type-selector">
              <div :class="['sample-chip', { active: filterParams.sampleTypes.includes('tumor') }]"
                @click="toggleSampleType('tumor')">
                <i :class="filterParams.sampleTypes.includes('tumor') ? 'el-icon-check' : ''"></i>
                Tumor
              </div>
              <div :class="['sample-chip', { active: filterParams.sampleTypes.includes('normal') }]"
                @click="toggleSampleType('normal')">
                <i :class="filterParams.sampleTypes.includes('normal') ? 'el-icon-check' : ''"></i>
                Normal
              </div>
            </div>
          </div>

          <!-- Cancer Type Selection -->
          <div class="parameter-group cancer-type-section">
            <label>
              Cancer Type
              <span v-if="filterParams.cancerTypes.length > 0" class="selection-info">
                <span class="selected-count">{{ filterParams.cancerTypes.length }}</span>
                <span class="clear-btn" @click.stop="clearCancerTypes">Clear</span>
              </span>
            </label>

            <div v-if="cancerTypesLoading" class="loading-state">
              <i class="el-icon-loading"></i>
            </div>

            <div v-else class="cancer-type-list">
              <div v-for="cancer in cancerTypes" :key="cancer.name"
                :class="['cancer-item', { selected: filterParams.cancerTypes.includes(cancer.name) }]"
                @click="toggleCancerType(cancer.name)">
                <span class="item-check">
                  <i v-if="filterParams.cancerTypes.includes(cancer.name)" class="el-icon-check"></i>
                </span>
                <span class="item-dot" :style="{ background: getTissueColor(cancer.tissue) }"></span>
                <span class="item-name">{{ cancer.name }}</span>
                <span class="item-count">{{ cancer.count }}</span>
              </div>
            </div>
          </div>

          <div class="parameter-footer">
            <el-button type="primary" @click="loadImages" :loading="loading">
              Load Images
            </el-button>
          </div>
        </div>
      </div>

      <!-- Results Panel -->
      <div class="results-panel">
        <!-- Table View -->
        <div v-if="!showImageView" class="table-view">
          <h4>
            <i class="el-icon-picture"></i> Assay Images
          </h4>
          
          <div v-if="loading" class="empty-state">
            <i class="el-icon-loading"></i>
            <p>Loading images...</p>
          </div>
          
          <div v-else-if="!hasLoaded" class="empty-state">
            <i class="el-icon-picture-outline"></i>
            <p>Select filters and load images</p>
            <span>Choose sample type and cancer types, then click "Load Images"</span>
          </div>
          
          <div v-else-if="tableData.length === 0" class="empty-state">
            <i class="el-icon-picture-outline"></i>
            <p>No images found</p>
            <span>Try adjusting your filters</span>
          </div>
          
          <div v-else class="table-wrapper">
            <el-table :data="tableData" style="width: 100%;" height="100%">
              <el-table-column label="Image" width="100" fixed>
                <template slot-scope="scope">
                  <img v-if="scope.row.thumbnail_base64"
                    :src="'data:image/jpeg;base64,' + scope.row.thumbnail_base64"
                    @click="viewFullImage(scope.row)"
                    class="thumbnail" />
                  <div v-else class="thumbnail-placeholder">
                    <i class="el-icon-picture-outline"></i>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="sampleId" label="Sample" width="120" fixed />
              <el-table-column prop="sampleType" label="Type" width="90">
                <template slot-scope="scope">
                  <span :class="['type-badge', scope.row.sampleType]">
                    {{ scope.row.sampleType === 'tumor' ? 'Tumor' : 'Normal' }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column prop="cancerType" label="Cancer Type" min-width="180">
                <template slot-scope="scope">
                  <span class="cancer-cell">
                    <span class="cell-dot" :style="{ background: getTissueColor(scope.row.tissue) }"></span>
                    {{ scope.row.cancerType }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column prop="datasetName" label="Dataset" min-width="150" />
              <el-table-column prop="magnification" label="Mag" width="70" />
              <el-table-column label="" width="70" fixed="right">
                <template slot-scope="scope">
                  <el-button type="text" size="small" @click="viewFullImage(scope.row)">View</el-button>
                </template>
              </el-table-column>
            </el-table>
            
            <div class="pagination-bar">
              <span class="total-badge">{{ totalImages }} images</span>
              <el-pagination
                small
                @size-change="handlePageSizeChange"
                @current-change="handlePageChange"
                :current-page="currentPage"
                :page-sizes="[10, 20, 50]"
                :page-size="pageSize"
                :total="totalImages"
                layout="sizes, prev, pager, next"
              />
            </div>
          </div>
        </div>

        <!-- Image View -->
        <div v-else class="image-view">
          <h4>
            <el-button type="text" icon="el-icon-back" @click="backToTable">Back</el-button>
            <span class="image-title">{{ currentImage.sampleId }}</span>
          </h4>
          <div v-if="fullImageLoading" class="empty-state">
            <i class="el-icon-loading"></i>
            <p>Loading full image...</p>
          </div>
          <div v-else class="image-container">
            <div class="image-meta">
              <span><strong>Dataset:</strong> {{ currentImage.datasetName }}</span>
              <span><strong>Type:</strong> {{ currentImage.sampleType === 'tumor' ? 'Tumor' : 'Normal' }}</span>
              <span><strong>Cancer:</strong> {{ currentImage.cancerType }}</span>
              <span><strong>Mag:</strong> {{ currentImage.magnification }}</span>
            </div>
            <div class="image-frame">
              <img v-if="currentImage.fullImage" :src="'data:image/jpeg;base64,' + currentImage.fullImage" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AssayImages',
  data() {
    return {
      filterParams: {
        cancerTypes: [],
        sampleTypes: ['tumor']
      },
      cancerTypes: [],
      cancerTypesLoading: false,
      loading: false,
      hasLoaded: false,
      tableData: [],
      totalImages: 0,
      totalPages: 1,
      currentPage: 1,
      pageSize: 20,
      showImageView: false,
      fullImageLoading: false,
      currentImage: {
        datasetId: null,
        datasetName: null,
        sampleId: null,
        sampleType: null,
        cancerType: null,
        tissue: null,
        magnification: null,
        fullImage: null
      }
    }
  },
  mounted() {
    this.loadCancerTypes();
  },
  methods: {
    async loadCancerTypes() {
      this.cancerTypesLoading = true;
      try {
        const response = await axios.get('/api/projectSearch/list_cancer_types');
        if (response.data.code === 200) {
          this.cancerTypes = response.data.data;
        }
      } catch (error) {
        console.error('Failed to load cancer types:', error);
      } finally {
        this.cancerTypesLoading = false;
      }
    },

    toggleSampleType(type) {
      const idx = this.filterParams.sampleTypes.indexOf(type);
      if (idx > -1) {
        if (this.filterParams.sampleTypes.length > 1) {
          this.filterParams.sampleTypes.splice(idx, 1);
        }
      } else {
        this.filterParams.sampleTypes.push(type);
      }
    },

    toggleCancerType(name) {
      const idx = this.filterParams.cancerTypes.indexOf(name);
      if (idx > -1) {
        this.filterParams.cancerTypes.splice(idx, 1);
      } else {
        this.filterParams.cancerTypes.push(name);
      }
    },

    clearCancerTypes() {
      this.filterParams.cancerTypes = [];
    },

    getTissueColor(tissue) {
      const colors = {
        'Kidney': '#dc2626',
        'Bladder': '#ea580c',
        'Lung': '#2563eb',
        'Breast': '#db2777',
        'Liver': '#7c3aed',
        'Brain': '#0891b2',
        'Colon': '#16a34a',
        'Prostate': '#475569',
        'Pancreas': '#d97706',
        'Ovary': '#9333ea',
        'Stomach': '#78350f',
        'Skin': '#f59e0b'
      };
      return colors[tissue] || '#64748b';
    },

    async loadImages() {
      this.loading = true;
      this.hasLoaded = true;
      this.currentPage = 1;
      
      try {
        const response = await axios.post('/api/projectSearch/get_assay_images', {
          cancerTypes: this.filterParams.cancerTypes,
          sampleTypes: this.filterParams.sampleTypes,
          page: this.currentPage,
          pageSize: this.pageSize
        });
        
        if (response.data.code === 200) {
          const data = response.data.data;
          
          if (data.total > 0) {
            this.tableData = data.items;
            this.totalImages = data.total;
            this.totalPages = data.totalPages;
            
            this.$notify({
              title: 'Success',
              message: `Found ${data.total} images`,
              type: 'success',
              position: 'top-right',
              duration: 2000
            });
          } else {
            this.tableData = [];
            this.totalImages = 0;
            this.totalPages = 1;
          }
        }
      } catch (error) {
        this.$notify({
          title: 'Error',
          message: 'Failed to load images: ' + error.message,
          type: 'error',
          position: 'top-right'
        });
      } finally {
        this.loading = false;
      }
    },

    async handlePageChange(page) {
      this.currentPage = page;
      await this.fetchPage();
    },

    async handlePageSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
      await this.fetchPage();
    },

    async fetchPage() {
      this.loading = true;
      try {
        const response = await axios.post('/api/projectSearch/get_assay_images', {
          cancerTypes: this.filterParams.cancerTypes,
          sampleTypes: this.filterParams.sampleTypes,
          page: this.currentPage,
          pageSize: this.pageSize
        });
        
        if (response.data.code === 200) {
          const data = response.data.data;
          this.tableData = data.items;
          this.totalImages = data.total;
          this.totalPages = data.totalPages;
        }
      } catch (error) {
        console.error('Failed to fetch page:', error);
      } finally {
        this.loading = false;
      }
    },

    async viewFullImage(row) {
      this.fullImageLoading = true;
      this.showImageView = true;
      this.currentImage = {
        datasetId: row.datasetId,
        datasetName: row.datasetName,
        sampleId: row.sampleId,
        sampleType: row.sampleType,
        cancerType: row.cancerType,
        tissue: row.tissue,
        magnification: row.magnification,
        fullImage: null
      };
      
      try {
        const response = await axios.post('/api/projectSearch/get_assay_full_image', {
          datasetId: row.datasetId,
          magnification: row.magnification,
          sampleId: row.sampleId
        });
        
        if (response.data.code === 200) {
          this.currentImage.fullImage = response.data.data.image_base64;
        } else {
          this.$notify({
            title: 'Error',
            message: response.data.error || 'Failed to load full image',
            type: 'error',
            position: 'top-right'
          });
          this.backToTable();
        }
      } catch (error) {
        this.$notify({
          title: 'Error',
          message: 'Failed to load full image: ' + error.message,
          type: 'error',
          position: 'top-right'
        });
        this.backToTable();
      } finally {
        this.fullImageLoading = false;
      }
    },

    backToTable() {
      this.showImageView = false;
      this.currentImage = {
        datasetId: null,
        datasetName: null,
        sampleId: null,
        sampleType: null,
        cancerType: null,
        tissue: null,
        magnification: null,
        fullImage: null
      };
    }
  }
}
</script>

<style scoped>
.app-container {
  background: #f8f9fa;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.module-layout {
  display: flex;
  flex-direction: row;
  padding: 16px;
  gap: 16px;
  overflow: hidden;
  flex: 1;
  min-height: 0;
}

/* Parameter Panel */
.parameter-panel {
  background: white;
  border-radius: 8px;
  width: 300px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.parameter-panel h4 {
  margin: 0;
  padding: 12px 16px;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.parameter-panel h4 i {
  color: #3b82f6;
}

.parameter-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.parameter-group {
  padding: 12px 16px;
}

.parameter-group label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;
}

.selection-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.selected-count {
  font-size: 12px;
  font-weight: 600;
  color: white;
  background: #16a34a;
  padding: 2px 8px;
  border-radius: 10px;
}

.clear-btn {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
}

.clear-btn:hover {
  color: #dc2626;
}

/* Sample Type Selector */
.sample-type-selector {
  display: flex;
  gap: 8px;
}

.sample-chip {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.15s;
  user-select: none;
}

.sample-chip:hover {
  background: #e2e8f0;
}

.sample-chip.active {
  background: #1e40af;
  color: white;
}

.sample-chip i {
  font-size: 12px;
  font-weight: 700;
}

/* Cancer Type List */
.cancer-type-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-top: 1px solid #e2e8f0;
}

.loading-state {
  padding: 24px;
  text-align: center;
  color: #94a3b8;
  font-size: 20px;
}

.cancer-type-list {
  flex: 1;
  overflow-y: auto;
}

.cancer-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 16px;
  cursor: pointer;
  transition: background 0.15s;
}

.cancer-item:hover {
  background: #f8fafc;
}

.cancer-item.selected {
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 6px;
  margin: 1px 7px;
  padding: 8px 8px;
  box-shadow: 0 1px 3px rgba(22, 163, 74, 0.15);
}

.item-check {
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
  color: #16a34a;
  font-size: 12px;
}

.item-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 5px;
}

.item-name {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: #334155;
  line-height: 1.4;
  word-break: break-word;
}

.item-count {
  font-size: 13px;
  font-weight: 600;
  color: #94a3b8;
  min-width: 24px;
  text-align: right;
  flex-shrink: 0;
}

.cancer-item.selected .item-name {
  color: #15803d;
}

.cancer-item.selected .item-count {
  color: #16a34a;
}

/* Parameter Footer */
.parameter-footer {
  padding: 12px 16px;
  border-top: 1px solid #e2e8f0;
  margin-top: auto;
}

.parameter-footer .el-button {
  width: 100%;
  font-size: 14px;
  font-weight: 600;
}

/* Results Panel */
.results-panel {
  flex: 1;
  background: white;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.results-panel h4 {
  margin: 0;
  padding: 12px 16px;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.results-panel h4 i {
  color: #3b82f6;
}

.total-badge {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}

.image-title {
  font-size: 14px;
  color: #64748b;
}

/* Table View */
.table-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.table-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.thumbnail {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.15s;
}

.thumbnail:hover {
  transform: scale(1.05);
}

.thumbnail-placeholder {
  width: 64px;
  height: 64px;
  background: #f1f5f9;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cbd5e1;
  font-size: 20px;
}

.type-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

.type-badge.tumor {
  background: #fef2f2;
  color: #b91c1c;
}

.type-badge.normal {
  background: #f0fdf4;
  color: #15803d;
}

.cancer-cell {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 13px;
  line-height: 1.4;
}

.cell-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 4px;
}

.pagination-bar {
  padding: 10px 16px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #64748b;
}

/* Image View */
.image-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.image-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 12px;
}

.image-meta {
  display: flex;
  gap: 20px;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 6px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #475569;
  flex-wrap: wrap;
}

.image-frame {
  flex: 1;
  background: #0f172a;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  padding: 16px;
}

.image-frame img {
  max-width: 100%;
  max-height: 100%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
}

/* Empty State */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  gap: 8px;
  padding: 48px;
}

.empty-state i {
  font-size: 48px;
  color: #cbd5e1;
}

.empty-state p {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
}

.empty-state span {
  font-size: 14px;
  color: #94a3b8;
  text-align: center;
  max-width: 300px;
}

/* Table Overrides */
::v-deep .el-table {
  font-size: 13px;
}

::v-deep .el-table th {
  background: #f8fafc !important;
  font-weight: 600;
  font-size: 12px;
  color: #475569;
  padding: 8px 0;
}

::v-deep .el-table td {
  padding: 6px 0;
}

/* Scrollbar */
.cancer-type-list::-webkit-scrollbar {
  width: 4px;
}

.cancer-type-list::-webkit-scrollbar-track {
  background: transparent;
}

.cancer-type-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

/* Responsive */
@media (max-width: 900px) {
  .module-layout {
    flex-direction: column;
  }

  .parameter-panel {
    width: 100%;
    max-height: 380px;
  }
}
</style>
